import React, { useState, useEffect } from 'react';
import { X, Shield, Loader2 } from 'lucide-react';
import guaranteeService from '../services/guaranteeService';
import { getFranchiseeIdForDataLoading } from '../App/services/appHelpers';

const WoodwormGuaranteeForm = ({ onClose, onSubmit, preselectedCustomer, preselectedJob, preselectedAppointment, user }) => {
  // Build treatment address from job
  const buildTreatmentAddress = () => {
    if (!preselectedJob) return '';
    const parts = [
      preselectedJob.treatment_address_line1,
      preselectedJob.treatment_address_line2,
      preselectedJob.treatment_address_city,
      preselectedJob.treatment_address_county,
      preselectedJob.treatment_address_postcode
    ].filter(Boolean);
    return parts.join(', ');
  };

  const [formData, setFormData] = useState({
    customer_name: preselectedCustomer?.display_name || preselectedCustomer?.company_name || '',
    date_issued: new Date().toISOString().split('T')[0],
    guarantee_years: 10,
    treatment_address: buildTreatmentAddress(),
    treatment_area: '',
    product_used: '0.25% w/w Permethrin',
    application_rate: 'low pressure: 210-350kPa, 30-50 lbs/in2',
    authorized_by: user?.name || '',
    company_name: '',
    company_address: '',
    company_reg_no: ''
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(true);

  // Fetch franchisee details (the customer's owner) on mount
  useEffect(() => {
    const fetchDetails = async () => {
      try {
        const franchiseeId = preselectedCustomer?.franchisee_id || getFranchiseeIdForDataLoading(user);
        if (!franchiseeId) return;
        const details = await guaranteeService.getFranchiseeDetails(franchiseeId);
        setFormData(prev => ({
          ...prev,
          authorized_by: details.authorized_by || prev.authorized_by,
          company_name: details.company_name || '',
          company_address: details.company_address || '',
          company_reg_no: details.company_reg_no || ''
        }));
      } catch (error) {
        console.error('Failed to fetch franchisee details:', error);
      } finally {
        setLoadingDetails(false);
      }
    };
    fetchDetails();
  }, [preselectedCustomer, user]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: null }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.customer_name.trim()) newErrors.customer_name = 'Required';
    if (!formData.date_issued) newErrors.date_issued = 'Required';
    if (!formData.treatment_address.trim()) newErrors.treatment_address = 'Required';
    if (!formData.treatment_area.trim()) newErrors.treatment_area = 'Required';
    if (!formData.authorized_by || !formData.authorized_by.trim()) newErrors.authorized_by = 'Required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await onSubmit({
        ...formData,
        customer_id: preselectedCustomer?.id || null,
        job_id: preselectedJob?.id || null,
        appointment_id: preselectedAppointment?.id || null
      });
    } catch (error) {
      console.error('Guarantee submit error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = (field) =>
    `w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-pest-blue focus:border-pest-blue outline-none ${
      errors[field] ? 'border-red-400 bg-red-50' : 'border-gray-300'
    }`;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b bg-gradient-to-r from-pest-blue to-pest-teal rounded-t-xl">
          <div className="flex items-center gap-2 text-white">
            <Shield size={20} />
            <h2 className="text-lg font-bold">Woodworm Guarantee</h2>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-4">
          {loadingDetails && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 size={14} className="animate-spin" />
              Loading franchisee details...
            </div>
          )}

          {/* Customer Name — read-only, from preselected customer */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name</label>
            <input
              type="text"
              value={formData.customer_name}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 text-sm cursor-not-allowed"
            />
          </div>

          {/* Date & Years row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date Issued</label>
              <input
                type="date"
                value={formData.date_issued}
                onChange={(e) => handleChange('date_issued', e.target.value)}
                className={inputClass('date_issued')}
              />
              {errors.date_issued && <p className="text-xs text-red-500 mt-1">{errors.date_issued}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Guarantee Years</label>
              <select
                value={formData.guarantee_years}
                onChange={(e) => handleChange('guarantee_years', parseInt(e.target.value))}
                className={inputClass('guarantee_years')}
              >
                {Array.from({ length: 20 }, (_, i) => i + 1).map(n => (
                  <option key={n} value={n}>{n} {n === 1 ? 'Year' : 'Years'}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Treatment Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Treatment Address</label>
            <textarea
              value={formData.treatment_address}
              onChange={(e) => handleChange('treatment_address', e.target.value)}
              rows={2}
              className={inputClass('treatment_address')}
            />
            {errors.treatment_address && <p className="text-xs text-red-500 mt-1">{errors.treatment_address}</p>}
          </div>

          {/* Treatment Area */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Treatment Area</label>
            <input
              type="text"
              value={formData.treatment_area}
              onChange={(e) => handleChange('treatment_area', e.target.value)}
              placeholder="e.g. Ground floor joists and floorboards"
              className={inputClass('treatment_area')}
            />
            {errors.treatment_area && <p className="text-xs text-red-500 mt-1">{errors.treatment_area}</p>}
          </div>

          {/* Product & Application Rate */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Product Used</label>
            <input
              type="text"
              value={formData.product_used}
              onChange={(e) => handleChange('product_used', e.target.value)}
              className={inputClass('product_used')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Application Rate</label>
            <input
              type="text"
              value={formData.application_rate}
              onChange={(e) => handleChange('application_rate', e.target.value)}
              className={inputClass('application_rate')}
            />
          </div>

          {/* Authorized By — pre-populated with logged-in user, not editable */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Authorized By</label>
            <input
              type="text"
              value={formData.authorized_by}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700 text-sm cursor-not-allowed"
            />
          </div>

          {/* Company Details (read-only, from franchisee) */}
          {(formData.company_name || formData.company_address || formData.company_reg_no) && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 space-y-2">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Company Details (from franchisee)</p>
              {formData.company_name && (
                <div>
                  <label className="block text-xs text-gray-500 mb-0.5">Company Name</label>
                  <input type="text" value={formData.company_name} readOnly className="w-full px-3 py-1.5 border border-gray-200 rounded text-sm bg-gray-100 text-gray-700 cursor-not-allowed" />
                </div>
              )}
              {formData.company_address && (
                <div>
                  <label className="block text-xs text-gray-500 mb-0.5">Company Address</label>
                  <input type="text" value={formData.company_address} readOnly className="w-full px-3 py-1.5 border border-gray-200 rounded text-sm bg-gray-100 text-gray-700 cursor-not-allowed" />
                </div>
              )}
              {formData.company_reg_no && (
                <div>
                  <label className="block text-xs text-gray-500 mb-0.5">Company Reg No.</label>
                  <input type="text" value={formData.company_reg_no} readOnly className="w-full px-3 py-1.5 border border-gray-200 rounded text-sm bg-gray-100 text-gray-700 cursor-not-allowed" />
                </div>
              )}
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-5 py-4 border-t bg-gray-50 rounded-b-xl">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || loadingDetails}
            className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-pest-blue to-pest-teal hover:from-blue-700 hover:to-teal-600 rounded-lg transition-all disabled:opacity-50 flex items-center gap-2"
          >
            {submitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Creating...
              </>
            ) : (
              <>
                <Shield size={15} />
                Create Guarantee
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WoodwormGuaranteeForm;
